// Value (y) axis bounds that land on round numbers, so the first and last ticks read like the rest
// (100–400 in steps of 50, not 126–384 with uneven end gaps).

const NICE_STEPS = [1, 2, 2.5, 5, 10];

/** A round step (1, 2, 2.5 or 5 × 10ⁿ) that splits `range` into about `targetTicks` intervals. */
export function niceStep(range: number, targetTicks = 6): number {
  if (!Number.isFinite(range) || range <= 0) return 1;
  const raw = range / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = NICE_STEPS.find((s) => s * magnitude >= raw) ?? 10;
  return step * magnitude;
}

/**
 * The padded data range widened to whole steps. Data that never goes below zero keeps a zero floor; a flat line
 * still gets a visible band around it.
 */
export function niceAxisBounds(paddedMin: number, paddedMax: number, targetTicks = 6): { min: number; max: number; step: number } {
  let lo = paddedMin;
  let hi = paddedMax;
  // No usable data: an empty 0–100 axis, the same default the chart used before
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 100, step: 20 };
  if (hi - lo <= 0) {
    const pad = Math.abs(lo) * 0.1 || 1;
    lo -= pad;
    hi += pad;
  }
  const step = niceStep(hi - lo, targetTicks);
  // Rounded so float steps (0.1, 2.5) don't leave 0.30000000000000004 on the axis
  const round = (v: number) => Number(v.toPrecision(12));
  let min = round(Math.floor(lo / step) * step);
  const max = round(Math.ceil(hi / step) * step);
  if (paddedMin >= 0 && min < 0) min = 0;
  return { min, max, step };
}

/**
 * Chart.js ticks at every round step inside [min, max]. Pass the step niceAxisBounds returned for its own bounds,
 * so both ends land on a tick; unset (a zoomed range), the step niceStep picks for the range shown.
 */
export function roundTicks(min: number, max: number, step?: number, targetTicks = 6): Array<{ value: number }> {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [{ value: min }];
  if (!step || step <= 0) step = niceStep(max - min, targetTicks);
  const ticks: Array<{ value: number }> = [];
  const first = Math.ceil(min / step - 1e-9) * step;
  for (let v = first; v <= max + step * 1e-9 && ticks.length < 100; v += step) {
    ticks.push({ value: Number(v.toPrecision(12)) });
  }
  return ticks;
}
